// Install step (SPEC §4, §9): install BEFORE choosing the folder, because only an
// installed app keeps folder permission across visits automatically.
import { useEffect, useState } from 'react';

export type InstallState =
  | { kind: 'standalone' }                       // running in the installed app window
  | { kind: 'installed-elsewhere' }              // installed just now from this tab
  | { kind: 'promptable'; prompt: () => Promise<void> }
  | { kind: 'manual' };                          // no prompt event: browser menu or already installed

export const isStandalone = () => matchMedia('(display-mode: standalone)').matches;

export function useInstall(): InstallState {
  const [state, setState] = useState<InstallState>(() => (isStandalone() ? { kind: 'standalone' } : { kind: 'manual' }));

  useEffect(() => {
    if (isStandalone()) return;
    const onPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setState({
        kind: 'promptable',
        prompt: async () => {
          await e.prompt();
          const { outcome } = await e.userChoice;
          if (outcome === 'dismissed') setState({ kind: 'manual' });
        },
      });
    };
    const onInstalled = () => setState({ kind: 'installed-elsewhere' });
    addEventListener('beforeinstallprompt', onPrompt);
    addEventListener('appinstalled', onInstalled);
    return () => {
      removeEventListener('beforeinstallprompt', onPrompt);
      removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return state;
}
